import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, ApiClientError } from '../api/client';
import type { OnboardingStep, PlatformType, PlanType } from '../types';
import './OnboardingPage.css';

// ─── 플랫폼 정보 ─────────────────────────────────────────────────────────────

const PLATFORMS: { id: PlatformType; name: string; icon: string; desc: string }[] = [
  {
    id: 'cafe24',
    name: '카페24',
    icon: '🛒',
    desc: '국내 최대 쇼핑몰 플랫폼. OAuth로 간편 연결',
  },
  {
    id: 'imweb',
    name: '아임웹',
    icon: '🌐',
    desc: '홈페이지 + 쇼핑몰 통합 플랫폼',
  },
  {
    id: 'nhn',
    name: 'NHN 고도몰/샵바이',
    icon: '🏪',
    desc: 'NHN커머스 쇼핑몰 플랫폼',
  },
];

// ─── 플랜 정보 ────────────────────────────────────────────────────────────────

const PLANS: { id: PlanType; name: string; price: number; salary: string; features: string[]; recommended?: boolean }[] = [
  {
    id: 'starter',
    name: '스타터',
    price: 9900,
    salary: '월 59,000원 급여',
    features: ['1개 쇼핑몰 연결', '기본 워커 4종', '일 200건 주문 처리', '이메일 지원'],
  },
  {
    id: 'business',
    name: '비즈니스',
    price: 29900,
    salary: '월 179,000원 급여',
    features: ['3개 쇼핑몰 연결', '전체 워커 6종', '무제한 주문 처리', '멀티 플랫폼 동기화', '우선 지원'],
    recommended: true,
  },
  {
    id: 'pro',
    name: '프로',
    price: 59900,
    salary: '월 359,000원 급여',
    features: ['무제한 쇼핑몰 연결', '전체 워커 6종', '무제한 주문 처리', '전용 분석 리포트', '전담 매니저'],
  },
];

// ─── 단계별 컴포넌트 ──────────────────────────────────────────────────────────

interface StepPlatformProps {
  selected: PlatformType | null;
  onSelect: (platform: PlatformType) => void;
  onConnect: () => void;
  isLoading: boolean;
  error: string;
}

function StepPlatform({ selected, onSelect, onConnect, isLoading, error }: StepPlatformProps) {
  return (
    <div className="onboarding__step">
      <h2 className="onboarding__step-title">쇼핑몰 플랫폼을 선택해주세요</h2>
      <p className="onboarding__step-desc">
        오팀장이 관리할 쇼핑몰 플랫폼을 선택하고 OAuth로 연결합니다.
        연결 후 언제든지 추가 쇼핑몰을 연결할 수 있습니다.
      </p>

      <div className="platform-grid">
        {PLATFORMS.map((platform) => (
          <button
            key={platform.id}
            className={`platform-card ${selected === platform.id ? 'selected' : ''}`}
            onClick={() => onSelect(platform.id)}
            type="button"
          >
            <span className="platform-card__icon">{platform.icon}</span>
            <span className="platform-card__name">{platform.name}</span>
            <span className="platform-card__desc">{platform.desc}</span>
          </button>
        ))}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <button
        className="btn btn-primary btn-block btn-lg"
        onClick={onConnect}
        disabled={!selected || isLoading}
      >
        {isLoading ? <span className="spinner" /> : `${selected ? PLATFORMS.find(p => p.id === selected)?.name : ''} 연결하기`}
      </button>
    </div>
  );
}

interface StepProfileProps {
  mallName: string;
  description: string;
  onMallNameChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onNext: () => void;
  onBack: () => void;
  error: string;
}

function StepProfile({
  mallName,
  description,
  onMallNameChange,
  onDescriptionChange,
  onNext,
  onBack,
  error,
}: StepProfileProps) {
  return (
    <div className="onboarding__step">
      <h2 className="onboarding__step-title">쇼핑몰 프로필을 설정해주세요</h2>
      <p className="onboarding__step-desc">
        오팀장 AI가 쇼핑몰을 더 잘 이해할 수 있도록 기본 정보를 입력해주세요.
      </p>

      <div className="onboarding__form">
        <div className="form-group">
          <label className="form-label" htmlFor="mallName">
            쇼핑몰 이름 <span className="required">*</span>
          </label>
          <input
            id="mallName"
            type="text"
            className="form-input"
            placeholder="예: 홍길동 패션몰"
            value={mallName}
            onChange={(e) => onMallNameChange(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="description">쇼핑몰 소개</label>
          <textarea
            id="description"
            className="form-input"
            rows={3}
            placeholder="쇼핑몰의 주요 상품 카테고리, 타겟 고객 등을 간략히 설명해주세요."
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
          />
          <span className="form-hint">
            AI가 쇼핑몰 특성에 맞는 응대를 제공하는 데 활용됩니다.
          </span>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="onboarding__btn-row">
        <button className="btn btn-secondary" onClick={onBack} type="button">
          이전
        </button>
        <button
          className="btn btn-primary"
          onClick={onNext}
          disabled={!mallName.trim()}
          type="button"
        >
          다음
        </button>
      </div>
    </div>
  );
}

interface StepPlanProps {
  onComplete: (plan: PlanType) => void;
  onBack: () => void;
  isLoading: boolean;
  error: string;
}

function StepPlan({ onComplete, onBack, isLoading, error }: StepPlanProps) {
  const [selected, setSelected] = useState<PlanType>('starter');

  return (
    <div className="onboarding__step">
      <h2 className="onboarding__step-title">플랜을 선택해주세요</h2>
      <p className="onboarding__step-desc">
        모든 플랜은 <strong>14일 무료 체험</strong>으로 시작됩니다.
        체험 기간 중 언제든지 변경하거나 취소할 수 있습니다.
      </p>

      <div className="plan-grid">
        {PLANS.map((plan) => (
          <button
            key={plan.id}
            className={`plan-card ${selected === plan.id ? 'selected' : ''} ${plan.recommended ? 'recommended' : ''}`}
            onClick={() => setSelected(plan.id)}
            type="button"
          >
            {plan.recommended && (
              <span className="plan-card__badge">추천</span>
            )}
            <span className="plan-card__name">{plan.name}</span>
            <div className="plan-card__price">
              <span className="plan-card__price-amount">
                {plan.price.toLocaleString('ko-KR')}
              </span>
              <span className="plan-card__price-unit">원/월</span>
            </div>
            <span className="plan-card__salary">{plan.salary}</span>
            <ul className="plan-card__features">
              {plan.features.map((f) => (
                <li key={f}>✓ {f}</li>
              ))}
            </ul>
          </button>
        ))}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="onboarding__btn-row">
        <button className="btn btn-secondary" onClick={onBack} type="button">
          이전
        </button>
        <button
          className="btn btn-primary"
          onClick={() => onComplete(selected)}
          disabled={isLoading}
          type="button"
        >
          {isLoading ? <span className="spinner" /> : '오팀장 시작하기'}
        </button>
      </div>
    </div>
  );
}

// ─── 메인 온보딩 페이지 ───────────────────────────────────────────────────────

const STEPS: { id: OnboardingStep; label: string }[] = [
  { id: 'platform', label: '플랫폼 연결' },
  { id: 'profile', label: '프로필 설정' },
  { id: 'plan', label: '플랜 선택' },
];

export default function OnboardingPage() {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState<OnboardingStep>('platform');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // 상태
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformType | null>(null);
  const [mallName, setMallName] = useState('');
  const [description, setDescription] = useState('');

  const currentIndex = STEPS.findIndex((s) => s.id === currentStep);

  // ── Step 1: 플랫폼 OAuth 연결 ─────────────────────────────────────────────

  const handlePlatformConnect = async () => {
    if (!selectedPlatform) return;
    setIsLoading(true);
    setError('');

    try {
      const data = await apiClient.post<{ authUrl: string }>(
        '/shops/oauth/init',
        { platform: selectedPlatform }
      );
      // OAuth 팝업 또는 리다이렉트
      window.location.href = data.authUrl;
    } catch (err) {
      setError(
        err instanceof ApiClientError ? err.message : '플랫폼 연결 중 오류가 발생했습니다.'
      );
      setIsLoading(false);
    }
  };

  // ── Step 2: 프로필 저장 ───────────────────────────────────────────────────

  const handleProfileNext = () => {
    if (!mallName.trim()) return;
    setError('');
    setCurrentStep('plan');
  };

  // ── Step 3: 온보딩 완료 ───────────────────────────────────────────────────

  const handleComplete = async (plan: PlanType) => {
    setIsLoading(true);
    setError('');

    try {
      await apiClient.post('/onboarding/complete', {
        platform: selectedPlatform,
        mallName: mallName.trim(),
        description: description.trim(),
        plan,
      });
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiClientError ? err.message : '온보딩 완료 중 오류가 발생했습니다.'
      );
      setIsLoading(false);
    }
  };

  return (
    <div className="onboarding-page">
      <div className="onboarding__card card">
        {/* 헤더 */}
        <div className="onboarding__header">
          <div className="onboarding__logo">🤖</div>
          <h1 className="onboarding__title">오팀장 설정</h1>
        </div>

        {/* 스텝 인디케이터 */}
        <div className="onboarding__steps">
          {STEPS.map((step, index) => (
            <div
              key={step.id}
              className={`onboarding__step-indicator ${
                index < currentIndex
                  ? 'done'
                  : index === currentIndex
                  ? 'active'
                  : 'pending'
              }`}
            >
              <div className="onboarding__step-num">
                {index < currentIndex ? '✓' : index + 1}
              </div>
              <span className="onboarding__step-name">{step.label}</span>
              {index < STEPS.length - 1 && (
                <div className="onboarding__step-line" />
              )}
            </div>
          ))}
        </div>

        {/* 단계별 콘텐츠 */}
        {currentStep === 'platform' && (
          <StepPlatform
            selected={selectedPlatform}
            onSelect={setSelectedPlatform}
            onConnect={() => void handlePlatformConnect()}
            isLoading={isLoading}
            error={error}
          />
        )}
        {currentStep === 'profile' && (
          <StepProfile
            mallName={mallName}
            description={description}
            onMallNameChange={setMallName}
            onDescriptionChange={setDescription}
            onNext={handleProfileNext}
            onBack={() => setCurrentStep('platform')}
            error={error}
          />
        )}
        {currentStep === 'plan' && (
          <StepPlan
            onComplete={(plan) => void handleComplete(plan)}
            onBack={() => setCurrentStep('profile')}
            isLoading={isLoading}
            error={error}
          />
        )}
      </div>
    </div>
  );
}
